import type {
  DatabaseValue,
  DatabaseField,
  DatabaseFieldType,
  RawDatabaseValue
} from '@databases/types/database';

import { type GroupableFieldType } from '../grouping.ts';

/** Identifier for the special group that contains entries with no value on the group field. */
export const EMPTY_VALUE_GROUP_KEY = '__empty_value_group_key__';

/** A grouping of database entries that share a common value for the chosen field. */
export abstract class DatabaseGroup<Type extends DatabaseFieldType = GroupableFieldType> {
  readonly key: string;
  readonly groupField: DatabaseField<Type>;
  readonly fieldValue?: DatabaseValue<Type>;
  entryId?: string;
  isHidden: boolean;

  constructor(
    key: string,
    groupField: DatabaseField<Type>,
    fieldValue?: DatabaseValue<Type>,
    entryId?: string
  ) {
    this.key = key;
    this.groupField = groupField;
    this.fieldValue = fieldValue;
    this.entryId = entryId;
    this.isHidden = false;
  }

  get type(): DatabaseFieldType {
    return this.groupField.type;
  }

  get value(): RawDatabaseValue<Type> | undefined {
    return this.fieldValue?.value;
  }

  /** The value used for display in the UI. */
  abstract get displayValue(): string | number | undefined;

  /** The value used for sorting groups. */
  get sortValue(): string | number {
    return this.displayValue ?? '';
  }

  /** Whether this is the special group for entries with no value on the group field. */
  get isEmptyValueGroup(): boolean {
    return this.key === EMPTY_VALUE_GROUP_KEY;
  }
}
